import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

// reusable components
import { TableRow, TableCell } from "@/components/ui/table";

// utils and helpers
import { searchFilterStock } from "../utils/searchFilterStock";
import { deriveStockStatus, STOCK_STATUS_META } from "../utils/inventoryHelpers";
import { StatusBadge } from "@/utils/resolveStatusStyle";
import { formatCurrency } from "@/utils/formatCurrency";
import { fetchStock } from "@/services/catalogueApi";
import { useBusiness } from "@hooks/useBusiness";

export default function useStockTable() {
  const { businessId } = useBusiness();

  const {
    data: items = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["inventory-stock", businessId],
    queryFn: () => fetchStock(businessId),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get("search") || "";
  const categoryFilter = searchParams.get("category") || "All";
  const statusFilter = searchParams.get("stockStatus") || "All";

  const updateParam = useCallback(
    (key, val, emptyValue = "All") => {
      const params = new URLSearchParams(searchParams);
      if (val && val !== emptyValue) params.set(key, val);
      else params.delete(key);
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const updateSearchTerm = useCallback((val) => updateParam("search", val, ""), [updateParam]);
  const updateCategoryFilter = useCallback((val) => updateParam("category", val), [updateParam]);
  const updateStatusFilter = useCallback((val) => updateParam("stockStatus", val), [updateParam]);

  const filteredItems = useMemo(
    () => searchFilterStock(items, searchTerm, categoryFilter, statusFilter),
    [items, searchTerm, categoryFilter, statusFilter]
  );

  const categories = useMemo(
    () => [...new Set(items.map((i) => i.category).filter(Boolean))],
    [items]
  );

  const stockColumns = useMemo(
    () => [
      {
        accessorKey: "productName",
        header: "Product",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium text-[#1a1a1a]">{row.original.productName}</span>
            <span className="text-xs text-[#999]">{row.original.sku}</span>
          </div>
        ),
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: ({ row }) => row.original.category || "-",
      },
      {
        accessorKey: "location",
        header: "Location",
        cell: ({ row }) => row.original.location || "-",
      },
      {
        accessorKey: "onHand",
        header: "On Hand",
        cell: ({ row }) => row.original.onHand ?? 0,
      },
      {
        accessorKey: "committed",
        header: "Committed",
        cell: ({ row }) => row.original.committed ?? 0,
      },
      {
        accessorKey: "available",
        header: "Available",
        cell: ({ row }) => (
          <span className="font-semibold text-[#1a1a1a]">{row.original.available ?? 0}</span>
        ),
      },
      {
        accessorKey: "retailPrice",
        header: "Retail",
        cell: ({ row }) => formatCurrency(row.original.retailPrice),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge map={STOCK_STATUS_META} status={deriveStockStatus(row.original)} />
        ),
      },
    ],
    []
  );

  const renderStockRow = useCallback((row) => {
    return (
      <TableRow key={row.id} className="hover:bg-[#f6f6ff] transition-colors">
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} className="px-4 py-3 text-[13px] font-medium">
            {cell.getContext().column.columnDef.cell?.(cell.getContext()) ?? cell.renderCell()}
          </TableCell>
        ))}
      </TableRow>
    );
  }, []);

  return {
    allItems: items,
    items: filteredItems,
    categories,
    searchTerm,
    updateSearchTerm,
    categoryFilter,
    updateCategoryFilter,
    statusFilter,
    updateStatusFilter,
    refetch,
    stockColumns,
    renderStockRow,
    isLoading,
    isError,
  };
}
